/**
 * useScreenTracking Hook
 *
 * React hook for automatic screen/page view tracking.
 * Automatically logs screen views when the component mounts
 * or when specified dependencies change.
 */

import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import analytics from '../analyticsService';
import { SCREEN_NAMES } from '../events';
import { AnalyticsParams } from '../types';

interface UseScreenTrackingOptions {
  /**
   * The name of the screen to track.
   * If not provided, will be derived from the current route path.
   */
  screenName?: string;

  /**
   * The class/component name of the screen.
   * Useful for distinguishing between different implementations of the same route.
   */
  screenClass?: string;

  /**
   * Additional parameters to include with the screen view event.
   */
  params?: AnalyticsParams;

  /**
   * Whether to track the screen view automatically on mount.
   * Default: true
   */
  trackOnMount?: boolean;

  /**
   * Dependencies that trigger a new screen view event when changed.
   * Useful for tracking when significant content changes occur.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps?: any[];
}

/**
 * Map of route paths to screen name constants from events.ts.
 * This creates a single source of truth for screen tracking.
 * Add new routes here as the application grows.
 */
const ROUTE_TO_SCREEN_NAME = new Map<string, string>([
  // Dashboard
  // ['/', SCREEN_NAMES.DASHBOARD], - Note: '/' redirects to /studies
  ['/studies', SCREEN_NAMES.STUDIES_LIST],
  ['/centers', SCREEN_NAMES.ALL_CENTERS],
  ['/developer', SCREEN_NAMES.DEVELOPER_CENTER],
  ['/api-client', SCREEN_NAMES.API_CLIENT],

  // Auth
  ['/signin', SCREEN_NAMES.LOGIN],
  ['/signin/service', SCREEN_NAMES.SERVICE_LOGIN],
  ['/signin/two-factor', SCREEN_NAMES.TWO_FACTOR],
  ['/signin/terms', SCREEN_NAMES.TERMS_OF_USE],
  ['/termsofuse-static', SCREEN_NAMES.TERMS_OF_USE],
  ['/forgot_password', SCREEN_NAMES.FORGOT_PASSWORD],
  ['/reset/password', SCREEN_NAMES.RESET_PASSWORD],
  ['/enrollments/activate', SCREEN_NAMES.ACTIVATE_ENROLLMENT],
  ['/check2fa', SCREEN_NAMES.TWO_FACTOR],

  // Profile
  ['/profile', SCREEN_NAMES.PROFILE],
  ['/profile/centers', SCREEN_NAMES.PROFILE_CENTERS],
  ['/profile/security', SCREEN_NAMES.PROFILE_SECURITY],
  ['/profile/my', SCREEN_NAMES.MY_PROFILE],

  ['//termsofuse-static', SCREEN_NAMES.TERMS_OF_USE],
  ['/activation-instructions', SCREEN_NAMES.INSTRUCTION], // If you want to keep this screen name
]);

/**
 * Get a screen name from a route path.
 * Handles dynamic segments like :key, :studyId, etc.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function getScreenNameFromPath(pathname: string): string {
  // Check for exact match first using Map.get() for safe access
  const exactMatch = ROUTE_TO_SCREEN_NAME.get(pathname);
  if (exactMatch) {
    return exactMatch;
  }

  // Handle dynamic routes
  const pathParts = pathname.split('/').filter(Boolean);

  // Study viewer routes (e.g., /study/:key/night-1)
  if (pathParts[0] === 'study' && pathParts.length >= 2) {
    if (!pathParts[2]) return SCREEN_NAMES.STUDY_VIEWER;

    // Map study sub-routes to screen names
    const studyViewMap: Record<string, string> = {
      'all-night': SCREEN_NAMES.STUDY_ALL_NIGHTS,
      'night-n': SCREEN_NAMES.STUDY_NIGHT_N,
      surveys: SCREEN_NAMES.STUDY_SURVEY,
      interpretation: SCREEN_NAMES.STUDY_INTERPRETATION,
      'event-log': SCREEN_NAMES.STUDY_EVENT_LOG,
      'study-settings': SCREEN_NAMES.STUDY_SETTINGS,
      settings: SCREEN_NAMES.STUDY_SETTINGS,
      notes: SCREEN_NAMES.STUDY_NOTES,
      files: SCREEN_NAMES.STUDY_FILES,
    };

    // Handle night-N pattern (night-1, night-2, etc.)
    const view = pathParts[2].startsWith('night-') ? 'night-n' : pathParts[2];
    const studyScreen = Object.prototype.hasOwnProperty.call(studyViewMap, view)
      ? studyViewMap[view as keyof typeof studyViewMap]
      : null;
    return studyScreen || SCREEN_NAMES.STUDY_VIEWER;
  }

  // Center routes (e.g., /center/:key/members)
  if (pathParts[0] === 'center' && pathParts.length >= 2) {
    if (!pathParts[2]) return SCREEN_NAMES.CENTER_DETAILS;

    const centerViewMap: Record<string, string> = {
      members: SCREEN_NAMES.CENTER_MEMBERS,
      details: SCREEN_NAMES.CENTER_DETAILS,
      enrollments: SCREEN_NAMES.CENTER_ENROLLMENTS,
      security: SCREEN_NAMES.CENTER_SECURITY,
      'ip-snippets': SCREEN_NAMES.CENTER_IP_SNIPPETS,
      'change-log': SCREEN_NAMES.CENTER_CHANGE_LOG,
    };

    return (
      (Object.prototype.hasOwnProperty.call(centerViewMap, pathParts[2])
        ? centerViewMap[pathParts[2]]
        : null) || SCREEN_NAMES.CENTER_DETAILS
    );
  }

  // Data viewer routes
  if (pathParts[0] === 'data-viewer') {
    return SCREEN_NAMES.DATA_VIEWER;
  }

  // Fallback: convert path to title case
  return (
    pathname
      .split('/')
      .filter(Boolean)
      .map((segment) => {
        // Skip dynamic segments (those with numbers or UUIDs)
        if (/^[0-9a-f-]+$/i.test(segment)) {
          return '';
        }
        return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      })
      .filter(Boolean)
      .join(' - ') || 'Home'
  );
}

/**
 * Hook for tracking screen/page views in React components.
 *
 * @example
 * ```tsx
 * // Basic usage - automatically tracks based on route
 * function StudiesPage() {
 *   useScreenTracking();
 *   return <div>Studies</div>;
 * }
 *
 * // Custom screen name
 * function CustomPage() {
 *   useScreenTracking({ screenName: 'Custom Dashboard' });
 *   return <div>Custom</div>;
 * }
 *
 * // With additional parameters
 * function StudyDetailPage({ studyId }) {
 *   useScreenTracking({
 *     screenName: 'Study Detail',
 *     params: { study_id: studyId },
 *     deps: [studyId],
 *   });
 *   return <div>Study {studyId}</div>;
 * }
 * ```
 */
export function useScreenTracking(options: UseScreenTrackingOptions = {}): void {
  const location = useLocation();
  const { screenName, screenClass, params, trackOnMount = true, deps = [] } = options;

  useEffect(() => {
    if (!trackOnMount) {
      return;
    }

    const resolvedScreenName = screenName || getScreenNameFromPath(location.pathname);
    const resolvedScreenClass = screenClass || location.pathname;

    // TODO: (analytics) - Ask which of the below logs do we need? ScreenView or ScreenViewWeb?
    analytics.logScreenView({
      screenName: resolvedScreenName,
      screenClass: resolvedScreenClass,
      params: {
        ...params,
        path: location.pathname,
        search: location.search,
      },
    });

    // TODO: romoving this view_web as it is redundant to above event
    // Also log the web-specific screen view (matches myAir pattern)
    // analytics.logScreenViewWeb(resolvedScreenName);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, trackOnMount, screenName, screenClass, ...deps]);
}

// Removing UseRouteTracking as we are using UseScreenTracking which gives more control over screenName and screenClass
/**
 * Hook for tracking screen views with automatic route change detection.
 * Use this at the app level to track all route changes.
 *
 * @example
 * ```tsx
 * function App() {
 *   useRouteTracking();
 *   return <RouterProvider router={router} />;
 * }
 * ```
 */
// export function useRouteTracking(): void {
//   const location = useLocation();

//   useEffect(() => {
//     const screenName = getScreenNameFromPath(location.pathname);

//     analytics.logScreenView({
//       screenName,
//       screenClass: location.pathname,
//       params: {
//         path: location.pathname,
//         search: location.search,
//         hash: location.hash,
//       },
//     });
//   }, [location]);
// }

export default useScreenTracking;
